<script setup lang="ts">
  import { onMounted, onUnmounted, watch, ref, computed } from 'vue';
  import { useListboxNavigation } from '@/shared/lib/use';
  import { IconImage } from '@/shared/ui';
  import ArrowDown from '@/shared/assets/icons/ArrowDown.svg';
  import SelectInputOptions from './SelectInputOptions.vue';

  interface Option {
    id: string | number;
    name: string;
    disabled?: boolean;
  }

  interface Props {
    modelValue: string | number;
    options: Option[];
    label?: string;
    icon?: string;
    disabled?: boolean;
    containerClass?: string;
    buttonClass?: string;
    shadowClass?: string;
    arrowRight?: string;
    arrowClass?: string;
    maxHeight?: string;
    useTeleport?: boolean;
    ariaLabelledby?: string;
    ariaLabel?: string;
    /** When false, a `modelValue` outside `options` is left alone instead of snapping to the
     * first option. Set it whenever `options` arrives asynchronously: until the list lands, a
     * value the caller restored (from the URL, say) matches nothing, and the snap would emit
     * over it and destroy the restored state. Callers with such a list own the fallback for a
     * value that turns out to be invalid once the real options are in. */
    autoSelectFirst?: boolean;
  }

  const props = withDefaults(defineProps<Props>(), {
    containerClass: 'w-full',
    buttonClass: 'bg-white-01 border border-[var(--color-border-color)] whitespace-nowrap',
    shadowClass: '',
    arrowRight: 'right-4',
    arrowClass: 'w-4 h-4',
    maxHeight: '240px',
    useTeleport: false,
    autoSelectFirst: true,
  });

  const isDisabled = computed(() => props.disabled ?? false);

  const emit = defineEmits(['update:modelValue']);
  const dropdownRef = ref<HTMLDivElement>();
  const buttonRef = ref<HTMLButtonElement>();
  const {
    isOpen,
    activeIndex,
    listboxId,
    listId,
    activeDescendant,
    closeDropdown,
    toggleDropdown,
    realignActive,
    handleKeydown,
  } = useListboxNavigation({
    options: () => props.options,
    modelValue: () => props.modelValue,
    isDisabled: () => isDisabled.value,
    onSelect: (option) => selectOption(option),
    focusTrigger: () => buttonRef.value?.focus(),
  });
  const teleportedDropdownRef = ref<HTMLDivElement>();
  const dropdownPosition = ref({ top: 0, left: 0, width: 0, maxHeight: '' });

  const parsedMaxHeight = computed(() => {
    const raw = props.maxHeight ?? '';
    if (raw.endsWith('rem')) {
      const value = Number.parseFloat(raw);
      const rootFontSize = Number.parseFloat(getComputedStyle(document.documentElement).fontSize);
      return Number.isFinite(value) && Number.isFinite(rootFontSize) ? value * rootFontSize : 240;
    }
    const value = Number.parseFloat(raw);
    return Number.isFinite(value) ? value : 240;
  });

  const selectedOption = computed(() => {
    return props.options.find((option) => String(option.id) === String(props.modelValue));
  });

  const selectFirstOptionIfNeeded = () => {
    if (props.autoSelectFirst && props.options.length > 0 && !props.label) {
      const valueExists = props.options.some(
        (option) => String(option.id) === String(props.modelValue)
      );

      if (!valueExists) {
        emit('update:modelValue', props.options[0].id);
      }
    }
  };

  const updateDropdownPosition = () => {
    if (!props.useTeleport || !buttonRef.value) return;

    const rect = buttonRef.value.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const dropdownMaxHeight = parsedMaxHeight.value;
    const renderedDropdownHeight = teleportedDropdownRef.value?.scrollHeight ?? dropdownMaxHeight;
    const dropdownHeight = Math.min(renderedDropdownHeight, dropdownMaxHeight);

    const spaceBelow = viewportHeight - rect.bottom - 8;
    const spaceAbove = rect.top - 8;

    let top: number;
    let maxHeight: number;
    if (spaceBelow >= dropdownHeight || spaceBelow >= spaceAbove) {
      top = rect.bottom + 4;
      maxHeight = Math.min(dropdownMaxHeight, spaceBelow);
    } else {
      const availableAbove = Math.min(dropdownHeight, spaceAbove);
      top = Math.max(4, rect.top - availableAbove - 4);
      maxHeight = Math.min(dropdownMaxHeight, spaceAbove);
    }

    let left = rect.left;
    if (left + rect.width > viewportWidth) {
      left = viewportWidth - rect.width - 8;
    }

    dropdownPosition.value = {
      top,
      left: Math.max(8, left),
      width: rect.width,
      maxHeight: `${maxHeight}px`,
    };
  };

  const onOptionsChange = () => {
    selectFirstOptionIfNeeded();
    realignActive();
  };

  const selectOption = (option: Option) => {
    if (!option.disabled) {
      emit('update:modelValue', option.id);
      closeDropdown(true);
    }
  };

  const handleClickOutside = (e: Event) => {
    const target = e.target as Node;
    if (props.useTeleport) {
      if (
        buttonRef.value &&
        !buttonRef.value.contains(target) &&
        teleportedDropdownRef.value &&
        !teleportedDropdownRef.value.contains(target)
      ) {
        closeDropdown();
      }
    } else {
      if (dropdownRef.value && !dropdownRef.value.contains(target)) {
        closeDropdown();
      }
    }
  };

  const handleScroll = () => {
    if (isOpen.value && props.useTeleport) {
      updateDropdownPosition();
    }
  };

  const handleResize = handleScroll;

  const syncTeleportListeners = (newValue?: boolean, oldValue?: boolean) => {
    if (oldValue && !newValue) {
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
    } else if (!oldValue && newValue) {
      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', handleResize);
    }
  };

  onMounted(() => {
    selectFirstOptionIfNeeded();
    document.addEventListener('click', handleClickOutside);
    if (props.useTeleport) {
      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', handleResize);
    }
  });

  onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside);
    if (props.useTeleport) {
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
    }
  });

  watch(() => props.options, onOptionsChange);
  watch(
    () => isOpen.value,
    (newValue) => {
      if (newValue && props.useTeleport) {
        updateDropdownPosition();
      }
    },
    { flush: 'post' }
  );
  watch(() => props.useTeleport, syncTeleportListeners);
</script>

<template>
  <div
    ref="dropdownRef"
    :class="['relative', containerClass]"
  >
    <IconImage
      v-if="icon"
      :src="icon"
      :alt="icon"
      class="absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none z-10"
    />

    <button
      ref="buttonRef"
      type="button"
      role="combobox"
      :disabled="isDisabled"
      :aria-labelledby="ariaLabelledby"
      :aria-label="ariaLabel"
      aria-haspopup="listbox"
      :aria-expanded="isOpen"
      :aria-controls="isOpen ? listboxId : undefined"
      :aria-activedescendant="activeDescendant"
      :class="[
        'w-full px-4 py-2.5 rounded-lg text-gray-07 text-sm/5.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--theme-secondary-ink)] pr-12 z-10 text-left',
        buttonClass,
        shadowClass,
        icon ? 'pl-10' : 'pl-4',
        isOpen && buttonClass.includes('border') ? 'focus:border-gray-07' : '',
        isDisabled ? 'opacity-50 cursor-not-allowed' : '',
      ]"
      @click="toggleDropdown"
      @keydown="handleKeydown"
      @keyup.space.prevent
    >
      <span
        v-if="label && !selectedOption"
        class="text-gray-03 block w-full truncate"
        data-select-placeholder
      >
        {{ label }}
      </span>
      <span
        v-else-if="selectedOption"
        class="block w-full truncate"
      >
        {{ selectedOption.name }}
      </span>
      <!-- Nothing to show yet: an empty button would collapse to its padding, so the box keeps
       the height of one line until a matching option arrives. -->
      <span
        v-else
        class="block w-full truncate"
      >
        &nbsp;
      </span>
    </button>

    <IconImage
      :src="ArrowDown"
      alt="Seta dropdown"
      :class="[
        'absolute top-1/2 transform -translate-y-1/2 pointer-events-none',
        arrowClass,
        arrowRight,
      ]"
    />

    <template v-if="!useTeleport">
      <div
        v-if="isOpen && !disabled"
        :id="listboxId"
        role="listbox"
        :style="{ maxHeight: props.maxHeight }"
        class="absolute top-full left-0 right-0 mt-1 p-3 bg-white-01 border border-gray-02 rounded-2xl shadow-lg z-1000 overflow-y-auto"
      >
        <SelectInputOptions
          :model-value="props.modelValue"
          :options="options"
          :id-prefix="listId"
          :active-index="activeIndex"
          @select="selectOption"
        />
      </div>
    </template>

    <Teleport
      v-else-if="useTeleport"
      to="body"
    >
      <div
        v-if="isOpen && !disabled"
        :id="listboxId"
        ref="teleportedDropdownRef"
        role="listbox"
        :style="{
          position: 'fixed',
          top: `${dropdownPosition.top}px`,
          left: `${dropdownPosition.left}px`,
          width: `${dropdownPosition.width}px`,
          maxHeight: dropdownPosition.maxHeight,
        }"
        class="p-3 bg-white-01 border border-gray-02 rounded-2xl shadow-lg z-[9999] overflow-y-auto"
      >
        <SelectInputOptions
          :model-value="props.modelValue"
          :options="options"
          :id-prefix="listId"
          :active-index="activeIndex"
          @select="selectOption"
        />
      </div>
    </Teleport>
  </div>
</template>
